import React, { useEffect, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip,
  CircularProgress, Stack, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { DialogUI } from '../Interface';
import navOnlineService from '../../service/navOnline.service';
import NavOnlineManualAcceptance from './NavOnlineManualAcceptance';
import {
  formatNavOnlineDate, getNavOnlineInvoiceOperation, getNavOnlineOperation,
  getNavOnlineStatus, getNavOnlineValidationSeverity,
} from '../../utils/navOnlineStatus';

const Detail = ({ label, value }) => value !== null && value !== undefined && value !== '' && (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>{value}</Typography>
  </Box>
);

const failureStages = {
  INVOICE_XML_GENERATION: 'Számlaadatok előkészítése',
  SUPPLIER_TAX_NUMBER_VALIDATION: 'Kiállítói adószám ellenőrzése',
  TAX_NUMBER_VALIDATION: 'Adószámok ellenőrzése',
};

const NavOnlineHistoryDialog = ({ open, onClose, documentTypeId, documentId, documentNumber, onLoaded, canManuallyAccept = false }) => {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [accepting, setAccepting] = useState(false);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    if (!open || !documentTypeId || !documentId) return;
    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setError('');
    setHistory(null);

    navOnlineService.getInvoiceHistory(documentTypeId, documentId, controller.signal)
      .then((data) => {
        if (!active) return;
        setHistory(data);
        onLoaded?.(data);
      })
      .catch((reason) => {
        if (!active) return;
        setError(reason?.response?.data?.error?.message || reason?.message || 'A NAV előzmények lekérdezése nem sikerült.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [open, documentTypeId, documentId, refreshVersion, onLoaded]);

  const current = history?.current;
  const currentStatus = getNavOnlineStatus(current);
  const communications = history?.communications ?? [];
  const canRetry =
    canManuallyAccept && ['FAILED', 'RETRY_PENDING'].includes(current?.communicationStatus);

  const handleRetry = async () => {
    if (!canRetry || !current?.communicationId) return;

    setRetrying(true);
    setError('');
    try {
      const data = await navOnlineService.retryInvoice(documentTypeId, documentId, current.communicationId);
      setHistory(data);
      onLoaded?.(data);
    } catch (reason) {
      setError(reason?.response?.data?.error?.message || reason?.message || 'A NAV-újraküldés indítása nem sikerült.');
    } finally {
      setRetrying(false);
    }
  };

  return (
    <DialogUI
      open={open}
      onClose={onClose}
      headerContent={`NAV előzmények${documentNumber ? ` – ${documentNumber}` : ''}`}
      maxWidth="lg"
      fullWidth
      closeBtn
      actionsContent={
        <Stack direction="row" spacing={1}>
          {canRetry && (
            <Button type="button" variant="contained" onClick={handleRetry} disabled={loading || accepting || retrying}>
              {retrying ? 'Újraküldés…' : 'Újraküldés'}
            </Button>
          )}
          <Button
            type="button"
            onClick={() => setRefreshVersion((version) => version + 1)}
            disabled={loading || accepting || retrying}
          >
            Frissítés
          </Button>
        </Stack>
      }
    >
      <Stack spacing={2} sx={{ pt: 2 }}>
        {loading && (
          <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={22} />
            <Typography>NAV előzmények betöltése…</Typography>
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {history?.enabled === false && <Alert severity="info">A NAV-küldés jelenleg nincs engedélyezve.</Alert>}
        {history?.enabled && (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
              <Typography fontWeight={600}>Aktuális állapot</Typography>
              <Chip label={currentStatus.label} color={currentStatus.color} size="small" />
              <Chip label={history.environment === 'PRODUCTION' ? 'Éles környezet' : 'Tesztkörnyezet'} size="small" variant="outlined" />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
              <Detail label="Próbálkozás" value={current?.attempt} />
              <Detail label="Következő újraküldés" value={current?.nextRetryAt && formatNavOnlineDate(current.nextRetryAt)} />
              <Detail label="Utolsó változás" value={current?.updatedAt && formatNavOnlineDate(current.updatedAt)} />
              <Detail label="Tranzakcióazonosító" value={current?.transactionId} />
            </Box>
            {current?.errorMessage && <Alert severity="error">{current.errorCode ? `${current.errorCode}: ` : ''}{current.errorMessage}</Alert>}
            {current?.manualAcceptance && (
              <Alert severity="info">
                <Typography>Manuálisan elfogadott NAV-rögzítés</Typography>
                <Detail label="Elfogadta" value={current.manualAcceptance.acceptedByName || current.manualAcceptance.acceptedBy} />
                <Detail label="Elfogadás ideje" value={formatNavOnlineDate(current.manualAcceptance.acceptedAt)} />
                <Detail label="Indoklás" value={current.manualAcceptance.note} />
                <Detail label="NAV-hivatkozás" value={current.manualAcceptance.reference} />
              </Alert>
            )}
            {open && canManuallyAccept && ['FAILED', 'RETRY_PENDING'].includes(current?.communicationStatus) && (
              <NavOnlineManualAcceptance key={`${documentTypeId}:${documentId}:${current.communicationId}`}
                documentTypeId={documentTypeId} documentId={documentId} communicationId={current.communicationId}
                onBusyChange={setAccepting} onAccepted={(data) => { setHistory(data); onLoaded?.(data); }} />
            )}
            {communications.length === 0 && <Alert severity="info">Ehhez a bizonylathoz még nincs NAV-kommunikáció.</Alert>}
            {communications.length > 0 && <Typography variant="subtitle1">Kommunikációs előzmények ({communications.length})</Typography>}
            <Box>
              {communications.map((communication, index) => {
                const status = getNavOnlineStatus(communication);
                const validationMessages = communication.errors ?? [];
                const warnings = communication.metadata?.generationWarnings ?? [];
                const failureStage = communication.metadata?.failureStage;
                return (
                  <Accordion key={communication.id} defaultExpanded={index === 0 || validationMessages.length > 0}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <Typography variant="body2">{formatNavOnlineDate(communication.createdAt)}</Typography>
                        <Typography fontWeight={600}>{communication.status === 'MANUALLY_ACCEPTED' ? 'Manuális elfogadás' : getNavOnlineOperation(communication.operation)}</Typography>
                        <Chip label={status.label} color={status.color} size="small" variant="outlined" />
                        {validationMessages.length > 0 && <Chip label={`${validationMessages.length} validációs üzenet`} size="small" variant="outlined" />}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={2}>
                        {validationMessages.map((validation, messageIndex) => (
                          <Alert key={messageIndex} severity={getNavOnlineValidationSeverity(validation.resultCode)}>
                            {validation.code && <Typography variant="body2" fontWeight={600}>{validation.code}</Typography>}
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{validation.message}</Typography>
                            <Box sx={{ mt: 1, display: 'grid', gap: 1 }}>
                              <Detail label="Érintett mező" value={validation.tag} />
                              <Detail label="Érték" value={validation.value} />
                              <Detail label="Számla sora" value={validation.line} />
                              <Detail label="Eredeti számla" value={validation.originalInvoiceNumber} />
                            </Box>
                          </Alert>
                        ))}
                        {warnings.map((warning, warningIndex) => <Alert severity="warning" key={warningIndex}>{warning}</Alert>)}
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
                          <Detail label="Kommunikáció azonosítója" value={communication.id} />
                          <Detail label="Manuális elfogadás indoklása" value={communication.metadata?.manualAcceptance?.note} />
                          <Detail label="NAV-hivatkozás" value={communication.metadata?.manualAcceptance?.reference} />
                          <Detail label="Elfogadta" value={communication.metadata?.manualAcceptance?.acceptedByName || communication.metadata?.manualAcceptance?.acceptedBy} />
                          <Detail label="Elfogadás ideje" value={communication.metadata?.manualAcceptance?.acceptedAt && formatNavOnlineDate(communication.metadata.manualAcceptance.acceptedAt)} />
                          <Detail label="Előző kommunikáció" value={communication.parentCommunicationId} />
                          <Detail label="Lépés sorszáma" value={communication.sequence} />
                          <Detail label="Próbálkozás" value={communication.attempt} />
                          <Detail label="Számlaművelet" value={getNavOnlineInvoiceOperation(communication.invoiceOperation)} />
                          <Detail label="Hiba keletkezési lépése" value={failureStages[failureStage] ?? failureStage} />
                          <Detail label="HTTP-státusz" value={communication.httpStatus} />
                          <Detail label="NAV válaszkód" value={communication.navFuncCode} />
                          <Detail label="NAV számlastátusz" value={communication.navInvoiceStatus} />
                          <Detail label="Kérésazonosító" value={communication.requestId} />
                          <Detail label="Tranzakcióazonosító" value={communication.transactionId} />
                          <Detail label="Kommunikációs lánc" value={communication.correlationId} />
                          <Detail label="Sorba állítva" value={communication.queuedAt && formatNavOnlineDate(communication.queuedAt)} />
                          <Detail label="Küldés indult" value={communication.sendingAt && formatNavOnlineDate(communication.sendingAt)} />
                          <Detail label="Elküldve" value={communication.sentAt && formatNavOnlineDate(communication.sentAt)} />
                          <Detail label="Válasz érkezett" value={communication.responseReceivedAt && formatNavOnlineDate(communication.responseReceivedAt)} />
                          <Detail label="Feldolgozás ellenőrizve" value={communication.processingAt && formatNavOnlineDate(communication.processingAt)} />
                          <Detail label="Feldolgozva" value={communication.processedAt && formatNavOnlineDate(communication.processedAt)} />
                          <Detail label="Sikertelen lett" value={communication.failedAt && formatNavOnlineDate(communication.failedAt)} />
                          <Detail label="Következő újraküldés" value={communication.nextRetryAt && formatNavOnlineDate(communication.nextRetryAt)} />
                          <Detail label="Válaszidő" value={communication.durationMs != null ? `${communication.durationMs} ms` : null} />
                        </Box>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          </>
        )}
      </Stack>
    </DialogUI>
  );
};

export default NavOnlineHistoryDialog;
